import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Car, CheckCircle2, ChefHat, Clock, MapPin, StickyNote, Wifi, WifiOff } from 'lucide-react';
import { useScreenDocumentTitle } from '@/hooks/useScreenDocumentTitle';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/toast';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { getSocket } from '@/lib/socket';
import type { KitchenDisplayStation, KitchenQueueItem } from '@/types/restaurant';
import { formatQuantity } from '@/lib/utils';
import { Dropdown } from 'primereact/dropdown';
import { StatusBadge } from '@/components/ui/status-badge';
import { useAuthStore } from '@/stores/auth.store';

function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="text-lg font-mono text-muted-foreground">
      {time.toLocaleTimeString()}
    </span>
  );
}

function elapsedColor(minutes: number): string {
  if (minutes < 5) return 'text-success-text';
  if (minutes < 10) return 'text-warning-text';
  return 'text-destructive-text';
}

function elapsedLabel(minutes: number): string {
  if (minutes < 1) return '< 1m';
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

const ORDER_TYPE_TONES: Record<string, 'info' | 'primary' | 'warning'> = {
  DINE_IN: 'info',
  TAKEAWAY: 'primary',
  DELIVERY: 'warning',
};

export function KitchenDisplayPage() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('restaurant');
  const { hasPermission } = useAuthStore();
  const canViewCar = hasPermission('car-takeaway:read') || hasPermission('car-takeaway:manage') || hasPermission('car-takeaway:*');
  const { t: tNav } = useTranslation('nav');
  useScreenDocumentTitle(tNav('kitchen'));
  const [selectedStationId, setSelectedStationId] = useState<string>('');
  const [socketConnected, setSocketConnected] = useState(false);

  // Track socket connection state — both polling + event-based for instant updates
  useEffect(() => {
    const update = () => setSocketConnected(getSocket()?.connected ?? false);
    update(); // immediate
    const check = setInterval(update, 2000);
    const s = getSocket();
    s?.on('connect', update);
    s?.on('disconnect', update);
    return () => {
      clearInterval(check);
      s?.off('connect', update);
      s?.off('disconnect', update);
    };
  }, []);

  // Load all KDS stations for the station selector
  const { data: stations } = useQuery<KitchenDisplayStation[]>({
    queryKey: ['kds-stations'],
    queryFn: () => api.get('/kitchen/stations').then((r) => r.data?.data ?? r.data ?? []),
  });

  // Auto-select first station if only one exists
  useEffect(() => {
    if (stations && stations.length === 1 && !selectedStationId) {
      setSelectedStationId(stations[0].id);
    }
  }, [stations, selectedStationId]);

  const selectedStation = stations?.find((s) => s.id === selectedStationId);

  const { data: items } = useQuery<KitchenQueueItem[]>({
    queryKey: ['kitchen-queue', selectedStationId],
    queryFn: () => {
      const params = selectedStationId ? `?stationId=${selectedStationId}` : '';
      return api.get(`/kitchen/queue${params}`).then((r) => r.data?.data ?? r.data ?? []);
    },
    refetchInterval: 300000, // 5 min safety-net; real-time via WebSocket
  });

  // Real-time: refresh kitchen queue whenever a kitchen event arrives
  const handleKitchenUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['kitchen-queue'] });
  }, [queryClient]);
  useSocketEvent('kitchen.item_updated', handleKitchenUpdate);
  useSocketEvent('kitchen.order_updated', handleKitchenUpdate);
  useSocketEvent('dining_order.created', handleKitchenUpdate);  // New order → appear in KDS immediately
  useSocketEvent('dining_order.updated', handleKitchenUpdate);  // Status changes

  const updateStatusMutation = useMutation({
    mutationFn: ({ itemId, status }: { itemId: string; status: string }) =>
      api.patch(`/kitchen/items/${itemId}/status`, { status }),
    onSuccess: () => {
      toast.success('Item status updated');
      queryClient.invalidateQueries({ queryKey: ['kitchen-queue'] });
    },
    onError: (err: unknown) => {
      toast.error(getApiErrorMessage(err, 'Failed to update item status'));
    },
  });

  const visibleItems = (items ?? []).filter(
    (i) => i.kitchenStatus === 'PENDING' || i.kitchenStatus === 'PREPARING'
  );

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      {/* Header strip */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-card border-b border-border flex-shrink-0 gap-3">
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <ChefHat className="h-5 w-5 sm:h-6 sm:w-6 text-warning-text" />
          <div>
            <span className="text-base sm:text-xl font-bold block leading-tight">
              {selectedStation ? selectedStation.name : 'Kitchen Display'}
            </span>
            {selectedStation?.location && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {selectedStation.location.name}
              </span>
            )}
          </div>
        </div>

        {/* Station selector */}
        {stations && stations.length > 1 && (
          <Dropdown
            value={selectedStationId}
            onChange={(e) => setSelectedStationId(e.value)}
            options={[
              { label: 'All Stations', value: '' },
              ...stations.map((s) => ({
                label: `${s.name}${s.location ? ` · ${s.location.name}` : ''}`,
                value: s.id,
              })),
            ]}
            optionLabel="label"
            optionValue="value"
            placeholder="All Stations"
            className="max-w-[240px]"
          />
        )}

        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          {/* Live connection indicator */}
          <div className={`flex items-center gap-1.5 text-sm font-medium px-2 py-1 rounded-full ${socketConnected ? 'bg-success/50 text-success-text' : 'bg-destructive/50 text-destructive-text'}`}>
            {socketConnected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
            {socketConnected ? 'Live' : 'Offline'}
          </div>
          <span className="text-sm sm:text-base text-muted-foreground">
            {visibleItems.length} item{visibleItems.length !== 1 ? 's' : ''}
          </span>
          <LiveClock />
        </div>
      </header>

      {/* Main grid */}
      <main className="flex-1 p-3 sm:p-4">
        {visibleItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground min-h-[60vh]">
            <ChefHat className="h-20 w-20 text-muted-foreground" />
            <p className="text-2xl font-semibold text-muted-foreground">No pending items</p>
            <p className="text-base text-muted-foreground text-center max-w-sm">
              {selectedStation
                ? `Nothing pending at ${selectedStation.name}`
                : 'All caught up! Waiting for new orders…'}
            </p>
            {!stations?.length && (
              <div className="mt-4 bg-warning/30 border border-warning rounded-xl p-4 max-w-md text-center">
                <p className="text-warning-text font-medium mb-1">{t('kdsNoStationsConfigured')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('kdsSetupHint')}{' '}
                  <Link
                    to="/admin/master-data/restaurant-setup"
                    className="text-warning-text underline font-medium hover:text-warning-text"
                  >
                    {t('kdsSetupLink')}
                  </Link>
                </p>
              </div>
            )}
            <div className="mt-2 bg-card border border-border rounded-xl p-4 max-w-md text-sm text-muted-foreground">
              <p className="font-medium text-muted-foreground mb-2">{t('kdsHowOrdersTitle')}</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>{t('kdsHowOrdersStep1')}</li>
                <li>{t('kdsHowOrdersStep2')}</li>
                <li>{t('kdsHowOrdersStep3')}</li>
                <li>{t('kdsHowOrdersStep4')}</li>
              </ol>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
            {visibleItems.map((item) => (
              <div
                key={item.id}
                className={`rounded-xl p-4 flex flex-col gap-3 border ${
                  canViewCar && item.orderType === 'TAKEAWAY' && item.carArrivedAt
                    ? 'bg-primary/10 border-primary ring-2 ring-primary'
                    : item.kitchenStatus === 'PREPARING'
                      ? 'bg-warning/40 border-warning'
                      : 'bg-card border-border'
                }`}
              >
                {/* Order info */}
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold text-xl">#{item.orderNo}</span>
                  <StatusBadge
                    status={item.orderType}
                    tone={ORDER_TYPE_TONES[item.orderType] ?? 'secondary'}
                    label={item.orderType.replace('_', '-')}
                    solid
                    className="px-2 py-0.5 text-sm"
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {item.tableNo && (
                    <span className="text-base font-semibold text-muted-foreground">Table #{item.tableNo}</span>
                  )}
                  {item.orderType === 'TAKEAWAY' && item.carSlot && (
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-primary-text bg-primary/40 rounded px-1.5 py-0.5">
                      <Car className="h-4 w-4" /> {item.carSlot}
                    </span>
                  )}
                  {canViewCar && item.orderType === 'TAKEAWAY' && item.carArrivedAt && (
                    <StatusBadge
                      status="CAR_HERE"
                      tone="primary"
                      solid
                      icon={<Bell className="h-4 w-4" />}
                      label={t('carHere')}
                      className="text-sm font-bold"
                    />
                  )}
                  {canViewCar && item.orderType === 'TAKEAWAY' && (item.vehicleDescription || item.vehiclePlate) && (
                    <span className="text-sm font-semibold text-foreground">
                      {[item.vehicleDescription, item.vehiclePlate].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {canViewCar && item.orderType === 'TAKEAWAY' && item.runnerName && (
                    <span className="text-sm text-muted-foreground">{t('runnerLabel', { name: item.runnerName })}</span>
                  )}
                  {item.locationName && (
                    <span className="flex items-center gap-0.5 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      {item.locationName}
                    </span>
                  )}
                </div>

                {/* Product */}
                <div className="flex-1">
                  <p className="text-lg font-bold leading-tight">{item.productName}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center rounded-full bg-card/10 px-4 py-1 text-xl font-bold">
                      ×{item.quantity}
                    </span>
                  </div>
                </div>

                {/* Modifiers */}
                {item.modifiers.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {item.modifiers.map((m, i) => (
                      <span key={i} className="rounded bg-card/10 px-2 py-1 text-sm text-foreground">
                        {m.modifierName}: {m.optionName}
                      </span>
                    ))}
                  </div>
                )}

                {/* Raw material ingredients */}
                {item.recipeIngredients?.length > 0 && (
                  <div className="rounded-lg bg-card/70 border border-border p-2">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Ingredients</p>
                    <ul className="space-y-0.5">
                      {item.recipeIngredients.map((ing, i) => (
                        <li key={i} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{ing.name}</span>
                          <span className="text-warning-text font-mono font-medium">{formatQuantity(ing.quantity)} {ing.uom}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Notes */}
                {item.notes && (
                  <p className="flex items-start gap-1.5 text-sm italic text-foreground bg-card rounded p-1.5"><StickyNote className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{item.notes}</span></p>
                )}

                {/* Elapsed time */}
                <div className={`flex items-center gap-1.5 text-lg font-semibold ${elapsedColor(item.elapsedMinutes)}`}>
                  <Clock className="h-4 w-4" />
                  {elapsedLabel(item.elapsedMinutes)}
                </div>

                {/* Action button */}
                <div>
                  {item.kitchenStatus === 'PREPARING' && (
                    <button
                      className="w-full h-14 rounded-lg bg-success-solid hover:bg-success/90 active:bg-success/90 font-semibold text-lg text-success-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none inline-flex items-center justify-center gap-2"
                      onClick={() => updateStatusMutation.mutate({ itemId: item.id, status: 'READY' })}
                      disabled={updateStatusMutation.isPending}
                    >
                      <CheckCircle2 className="h-5 w-5" /> Mark Ready
                    </button>
                  )}
                  {item.kitchenStatus === 'PENDING' && (
                    <button
                      className="w-full h-14 rounded-lg bg-warning hover:bg-warning/90 active:bg-warning/90 font-semibold text-lg text-warning-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none"
                      onClick={() => updateStatusMutation.mutate({ itemId: item.id, status: 'PREPARING' })}
                      disabled={updateStatusMutation.isPending}
                    >
                      Start Preparing
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
